/* eslint-disable react/jsx-props-no-spreading */
import { useUser } from '@folklore/auth';
import { useForm } from '@folklore/forms';
import { useUrlGenerator } from '@folklore/routes';
import classNames from 'classnames';
import { camelCase } from 'lodash';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import useSetUser from '../../hooks/useSetUser';
import * as AppPropTypes from '../../lib/PropTypes';
import { getComponentFromName } from '../../lib/utils';

import { useThemeClassName } from '../../contexts/ThemeContext';
import SubmitButton from '../buttons/SubmitButton';
import FieldLabel from '../fields/FieldLabel';
import * as FieldComponents from '../fields/index';
import CircleCheck from '../icons/CircleCheck';

import styles from '../../../styles/forms/draw-form-entry.module.scss';

const propTypes = {
    draw: AppPropTypes.draw.isRequired,
    fields: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string,
            type: PropTypes.string,
        }),
    ),
    // eslint-disable-next-line react/forbid-prop-types
    value: PropTypes.object,
    note: PropTypes.string,
    completeMessage: PropTypes.node,
    submitLabel: PropTypes.string,
    // withEmailForm: PropTypes.bool,
    isCreate: PropTypes.bool,
    className: PropTypes.string,
    onSuccess: PropTypes.func,
};

const defaultProps = {
    fields: null,
    value: null,
    note: null,
    completeMessage: null,
    submitLabel: null,
    // withEmailForm: false,
    isCreate: false,
    className: null,
    onSuccess: null,
};

function DrawFormEntry({
    draw,
    fields,
    value,
    completeMessage,
    note,
    submitLabel,
    isCreate,
    className,
    onSuccess,
}) {
    const themeClassName = useThemeClassName(styles);
    const { id: drawId } = draw;
    const route = useUrlGenerator();
    const action = route('draw.form_entry', {
        draw: drawId,
    });
    const setUser = useSetUser();

    const onComplete = useCallback(
        (newUser) => {
            setUser(newUser);
            if (onSuccess !== null) {
                onSuccess(newUser);
            }
        },
        [setUser, onSuccess],
    );

    const fieldsNames = useMemo(() => fields.map(({ name }) => name), [fields]);

    const initialFieldsValue = useMemo(
        () =>
            fields.reduce(
                (map, { name }) => ({
                    ...map,
                    [name]: value !== null ? value[camelCase(name)] : null,
                }),
                {},
            ),
        [value, fields],
    );

    const {
        fields: formFields,
        onSubmit,
        errors,
        loading,
        generalError = null,
    } = useForm({
        fields: fieldsNames,
        initialValue: initialFieldsValue,
        action,
        onComplete,
    });

    if (value !== null && completeMessage !== null) {
        return (
            <div
                className={classNames([
                    styles.container,
                    {
                        [styles.loading]: loading,
                    },
                    themeClassName,
                    className,
                ])}
            >
                <div className={styles.inner}>
                    <div className={styles.label}>{completeMessage}</div>
                    {fields.map(({ name }) => {
                        const fieldValue = value[name] || null;
                        if (fieldValue === null) {
                            return null;
                        }
                        return (
                            <div key={`field-value-${name}`} className={styles.answer}>
                                {fieldValue}
                                <CircleCheck className={styles.icon} />
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    }

    return (
        <form
            className={classNames([
                styles.container,
                {
                    [styles.loading]: loading,
                },
                themeClassName,
                className,
            ])}
            onSubmit={onSubmit}
            action={action}
        >
            <div className={styles.inner}>
                {fields.map((field) => {
                    const { name = null, type = null, label = null, ...fieldProps } = field;
                    const fieldErrors = (errors || {})[name] || null;
                    const FieldComponent = getComponentFromName(FieldComponents, type);
                    return FieldComponent !== null ? (
                        <div
                            key={`field-${name}`}
                            className={classNames([
                                styles.formGroup,
                                {
                                    [styles.hasError]: fieldErrors !== null,
                                },
                            ])}
                        >
                            {label !== null ? (
                                <FieldLabel className={styles.label}>{label}</FieldLabel>
                            ) : null}
                            <FieldComponent
                                className={styles.input}
                                {...fieldProps}
                                {...formFields[name]}
                            />
                            {fieldErrors !== null ? (
                                <div className={styles.errors}>{fieldErrors.join(<br />)}</div>
                            ) : null}
                        </div>
                    ) : null;
                })}

                {note !== null ? <div className={styles.note}>* {note}</div> : null}

                <SubmitButton className={styles.submit}>
                    {submitLabel !== null && isCreate ? (
                        <p>{submitLabel}</p>
                    ) : (
                        <FormattedMessage defaultMessage="Validate" description="Button label" />
                    )}
                </SubmitButton>
            </div>
            <div className={styles.errors}>
                {generalError !== null ? <div>{generalError}</div> : null}
            </div>
        </form>
    );
}

DrawFormEntry.propTypes = propTypes;
DrawFormEntry.defaultProps = defaultProps;

export default DrawFormEntry;
